import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { StatusBadge, EnvBadge, errMsg } from "../components/takedown/TakedownBits";

const inputCls = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white";
const MAX_BULK = 500;

export default function ContentProtectionDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [notices, setNotices] = useState(null);
  const [events, setEvents] = useState(null);
  const [error, setError] = useState("");
  const [url, setUrl] = useState("");
  const [bulk, setBulk] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setError("");
      const base = `/api/takedowns/projects/${id}`;
      const [p, n, a] = await Promise.all([api.get(base), api.get(`${base}/notices`), api.get(`${base}/audit`)]);
      setProject(p.data); setNotices(n.data); setEvents(a.data);
    } catch (e) {
      setError(errMsg(e, "Failed to load campaign"));
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const submitOne = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    if (!url.trim().startsWith("https://")) return toast.error("Only HTTPS URLs are allowed");
    setBusy(true);
    try {
      const { data } = await api.post("/api/takedowns/notices", { project_id: id, url: url.trim() });
      toast.success(data.remote_submitted ? "Notice submitted" : "Notice saved (not yet sent to Takedown)");
      setUrl(""); load();
    } catch (err) { toast.error(errMsg(err, "Could not submit notice")); }
    finally { setBusy(false); }
  };

  const submitBulk = async () => {
    const urls = bulk.split(/\s+/).filter(Boolean);
    if (!urls.length) return toast.error("Paste at least one URL");
    if (urls.length > MAX_BULK) return toast.error(`Maximum ${MAX_BULK} URLs`);
    const bad = urls.find((u) => !u.startsWith("https://"));
    if (bad) return toast.error(`Only HTTPS URLs are allowed: ${bad}`);
    setBusy(true);
    try {
      const { data } = await api.post("/api/takedowns/notices/bulk", { project_id: id, urls });
      toast.success(`${data.created.length} submitted, ${data.skipped.length} skipped`);
      setBulk(""); load();
    } catch (err) { toast.error(errMsg(err, "Bulk submission failed")); }
    finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (f) setBulk(await f.text());
    e.target.value = "";
  };

  const active = project && project.status !== "failed";

  return (
    <AppLayout
      title={project?.stage_name || "Campaign"}
      overline="CONTENT PROTECTION"
      actions={project && <EnvBadge environment={project.environment} />}
    >
      <div className="p-4 sm:p-6 md:p-10 space-y-8 max-w-5xl">
        <Link to="/crm/content-protection" className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1"><ArrowLeft size={14} /> All campaigns</Link>

        {error && <div className="text-red-300 text-sm" data-testid="detail-error">{error}</div>}
        {!project && !error && <div className="text-gray-400 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}

        {project && (
          <>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-2" data-testid="campaign-detail">
              <div className="flex items-center gap-3"><StatusBadge status={project.status} /><span className="text-xs text-gray-500">Takedown ID: {project.takedown_id || "—"}</span></div>
              {project.error && <div className="text-sm text-red-300">Error: {project.error.message}</div>}
              <div className="text-xs text-gray-500">Created {new Date(project.created_at).toLocaleString()} by {project.submitted_by}</div>
            </div>

            {active ? (
              <div className="grid md:grid-cols-2 gap-4">
                <form onSubmit={submitOne} className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-3">
                  <h2 className="font-semibold">Submit infringing URL</h2>
                  <input className={inputCls} placeholder="https://…" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="notice-url-input" />
                  <button disabled={busy} className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-sm font-semibold disabled:opacity-50" data-testid="submit-notice-btn">Submit</button>
                </form>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-3">
                  <h2 className="font-semibold">Bulk submit</h2>
                  <textarea rows={4} className={inputCls} placeholder="One HTTPS URL per line" value={bulk} onChange={(e) => setBulk(e.target.value)} data-testid="bulk-input" />
                  <div className="flex items-center gap-3">
                    <button onClick={submitBulk} disabled={busy} className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-sm font-semibold disabled:opacity-50" data-testid="submit-bulk-btn">Submit all</button>
                    <input type="file" accept=".txt,.csv,text/plain" onChange={onFile} className="text-xs text-gray-400" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-400">This campaign failed; notices cannot be submitted.</div>
            )}

            <section>
              <h2 className="font-semibold mb-3">Notices</h2>
              {notices === null ? null : notices.length === 0 ? (
                <div className="text-gray-500 text-sm border border-dashed border-white/10 rounded-xl p-6 text-center" data-testid="notices-empty">No notices submitted yet.</div>
              ) : (
                <div className="space-y-2">
                  {notices.map((n) => (
                    <div key={n.id} className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 flex items-center justify-between gap-3" data-testid="notice-row">
                      <div className="min-w-0">
                        <div className="text-sm truncate">{n.url}</div>
                        {n.error && <div className="text-xs text-red-300">{n.error.message}</div>}
                      </div>
                      <StatusBadge status={n.status} />
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section>
              <h2 className="font-semibold mb-3">Activity</h2>
              {events === null ? null : events.length === 0 ? (
                <div className="text-gray-500 text-sm" data-testid="audit-empty">No activity yet.</div>
              ) : (
                <ol className="border-l border-white/10 ml-2 space-y-4" data-testid="audit-timeline">
                  {events.map((ev) => (
                    <li key={ev.id} className="pl-4 relative">
                      <span className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-purple-400" />
                      <div className="text-sm">{ev.message}</div>
                      <div className="text-xs text-gray-500">{new Date(ev.created_at).toLocaleString()} · {ev.actor} · {ev.event_type}</div>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          </>
        )}
      </div>
    </AppLayout>
  );
}
